import { useRef, useState, useEffect } from 'react';
import { Action, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import intl from 'react-intl-universal';
import { useNavigate, useParams } from 'react-router-dom';

import saveAs from 'file-saver';

import { FormikProps } from 'formik';

import { Grid2 as Grid, LinearProgress } from '@mui/material';

import { Card } from '@/models/card/CardModel.ts';
import { Comment } from '@/models/CommentModel.ts';

import { selectActiveBoard, selectRoomMembers } from '../../ducks/Boards';
import {
  selectDelivery,
  selectAttachments,
  selectComments,
  selectLog,
  selectIsUploadingAttachments,
  selectIsDeletingAttachments,
  actions as deliveriesActions,
  selectPossibleResponsible,
  selectIsLoadingPossibleResponsible,
  selectPossibleResponsibleRequested,
} from '../../ducks/Deliveries';
import { downloadDeliveryAttachment } from '../../services/Deliveries';
import { alertAction } from '../../thunks/Alerts';
import { fetchRoomMembers } from '../../thunks/Boards';
import * as deliveryActions from '../../thunks/Deliveries';
import {
  calculateDisabledFieldsForDelivery,
  getRequiredFields,
  getUpdatedFieldsAndValues,
  populateInitialValues,
  getCardMetaInfo,
  hasResponsibleField,
  isFieldChanged,
} from '../../utils/FieldUtil';
import { LIST_DELIVERY_VIEW_MODE, withViewModeQuery } from '../../utils/ViewModeUtil';

import DeliveryAttachments from '../common/Attachments';
import DeliveryComments from '../common/Comments';
import DeleteDeliveryDialogue from '../../common/DeleteCardItemDialog';
import DeleteIconButton from '../../common/DeleteIconButton';
import Error from '../../common/Error';
import Form from '../common/Form';
import Log from '../common/Log';
import PageWrapper from '../../common/PageWrapper';

const mapStateToProps = (state: any, ownProp: any) =>
  createStructuredSelector({
    board: selectActiveBoard(),
    delivery: selectDelivery({ deliveryId: ownProp.params.deliveryId }),
    attachments: selectAttachments(),
    log: selectLog(),
    isUploadingAttachments: selectIsUploadingAttachments(),
    isDeletingAttachments: selectIsDeletingAttachments(),
    comments: selectComments(),
    roomMembers: selectRoomMembers(),
    possibleResponsible: selectPossibleResponsible(),
    isLoadingPossibleResponsible: selectIsLoadingPossibleResponsible(),
    possibleResponsibleRequested: selectPossibleResponsibleRequested(),
  });

const mapDispatchToProps = (dispatch: any) => ({
  actions: bindActionCreators({ ...deliveryActions, ...deliveriesActions, fetchRoomMembers }, dispatch),
});

const DeliveryUpdateContainer = ({
  board,
  delivery,
  log,
  attachments,
  isUploadingAttachments,
  isDeletingAttachments,
  comments,
  roomMembers,
  possibleResponsible,
  isLoadingPossibleResponsible,
  possibleResponsibleRequested,
  actions,
}: any) => {
  const { communityId, roomId, boardId, deliveryId } = useParams();
  const navigate = useNavigate();
  const formRef = useRef<FormikProps<any>>();
  const [openDeleteDialogue, setOpenDeleteDialogue] = useState(false);
  const [closeOnSuccess, setCloseOnSuccess] = useState(false);
  const hasResponsible = !!hasResponsibleField({ fieldConfig: board.delivery_config });

  useEffect(() => {
    actions
      .fetchComments({ communityId, roomId, boardId, deliveryId })
      .then((action: Action) => alertAction({ action, error: intl.get('common.comments.fetch.error') }));
    actions
      .fetchAttachments({ communityId, roomId, boardId, deliveryId })
      .then((action: Action) => alertAction({ action, error: intl.get('common.attachments.fetch.error') }));
    actions
      .fetchLog({ communityId, roomId, boardId, cardId: deliveryId })
      .then((action: Action) => alertAction({ action, error: intl.get('common.log.fetch.error') }));

    if (hasResponsible && !isLoadingPossibleResponsible && possibleResponsibleRequested.boardId != boardId) {
      actions.fetchPossibleResponsible({ communityId, roomId, boardId });
    }
  }, [communityId, roomId, boardId, deliveryId]);

  const handleSubmit = ({ values, item }: { values: any; item: any }) => {
    const data = {
      type: 'entry',
      fields: getUpdatedFieldsAndValues({
        values,
        item,
        fieldsConfig: board.delivery_config.fields,
        fieldChangePredicate: isFieldChanged,
      }),
    };

    return actions.updateDelivery({ communityId, roomId, boardId, deliveryId: item.id, data }).then((action: any) =>
      alertAction({
        action,
        error: intl.get('delivery.update.error'),
        success: intl.get('delivery.update.success'),
        onSuccess: () =>
          navigate(
            withViewModeQuery(
              closeOnSuccess
                ? `/${communityId}/${roomId}/boards/${board.id}`
                : `/${communityId}/${roomId}/boards/${board.id}/deliveries/${action.payload.delivery.id}`,
            ),
          ),
      }),
    );
  };

  const handleCancel = () => {
    navigate(withViewModeQuery(`/${communityId}/${roomId}/boards/${board.id}`));
  };

  const handleSubmitComment = ({ content }: { content: any }) =>
    actions.createComment({ communityId, roomId, boardId, deliveryId: delivery.id, content }).then((action: Action) => {
      alertAction({
        action,
        error: intl.get('common.comments.add.error'),
        success: intl.get('common.comments.add.success'),
      });
    });

  const handleUpdateComment = ({ comment }: { comment: Comment }) =>
    actions
      .updateComment({ communityId, roomId, boardId, deliveryId, commentId: comment.id, comment })
      .then((action: Action) =>
        alertAction({
          action,
          error: intl.get('common.comments.update.error'),
          success: intl.get('common.comments.update.success'),
        }),
      );

  const handleDeleteComment = ({ comment }: { comment: Comment }) =>
    actions.deleteComment({ communityId, roomId, boardId, deliveryId, commentId: comment.id }).then((action: Action) =>
      alertAction({
        action,
        error: intl.get('common.comments.delete.error'),
        success: intl.get('common.comments.delete.success'),
      }),
    );

  const handleUploadAttachment = ({ files }: { files: any }) => {
    if (files.length > 0) {
      actions.uploadAttachment({ communityId, roomId, boardId, deliveryId, files }).then((action: Action) =>
        alertAction({
          action,
          success: intl.get('common.attachments.upload.success'),
          error: intl.get('common.attachments.upload.error'),
        }),
      );
    }
  };

  const handleAddLinks = (links: any) =>
    links.forEach((link: any) =>
      actions.addDeliveryLink({ communityId, roomId, boardId, deliveryId, link }).then((action: Action) =>
        alertAction({
          action,
          error: intl.get('common.link.add.error'),
          success: intl.get('common.link.add.success'),
        }),
      ),
    );

  const handleDownloadAttachment = ({ attachment }: { attachment: any }) => {
    let attachmentId = attachment.id;
    let version;
    if (attachment.type === 'internal-link') {
      attachmentId = attachment.destination;
      version = attachment.destination_version;
    }

    downloadDeliveryAttachment({ communityId, roomId, boardId, deliveryId, attachmentId, version }).then((response) => {
      saveAs(response.data, attachment.name);
    });
  };

  const handleDeleteAttachment = ({ attachmentId }: { attachmentId: string }) =>
    actions.deleteAttachment({ communityId, roomId, boardId, deliveryId, attachmentId }).then((action: Action) =>
      alertAction({
        action,
        error: intl.get('common.attachments.delete.error'),
        success: intl.get('common.attachments.delete.success'),
      }),
    );

  const handleDelete = () => {
    actions.deleteDelivery({ communityId, roomId, boardId, delivery }).then((action: Action) =>
      alertAction({
        action,
        success: intl.get('board.tooltips.delivery.delete.success.message'),
        error: intl.get('board.tooltips.delivery.delete.error.message'),
        onSuccess: () => {},
      }),
    );

    navigate(`/${communityId}/${roomId}/boards/${boardId}?view=${LIST_DELIVERY_VIEW_MODE}`);
  };

  const deleteIconButton = (delivery: Card) => (
    <DeleteIconButton
      tooltipText={intl.get('board.tooltips.delivery.delete.question.title')}
      onClick={() => setOpenDeleteDialogue(true)}
      disabled={!delivery.permissions.DELETE}
    />
  );

  const handleSaveClick = (closeForm: boolean) => {
    const form = formRef.current;
    setCloseOnSuccess(closeForm);

    if (form) {
      form.handleSubmit();
    }
  };

  // Failed to fetch delivery.
  if (!delivery) {
    return <Error text={intl.get('delivery.not_found')} />;
  }

  if (hasResponsible && (isLoadingPossibleResponsible || possibleResponsibleRequested.boardId != boardId)) {
    return <LinearProgress />;
  }

  return (
    <PageWrapper title={intl.get('app_bar.delivery_card')}>
      <Grid container spacing={2}>
        <Grid size={{ sm: 12, md: 8, xs: 12 }}>
          <Form
            formRef={formRef}
            item={delivery}
            initialValues={populateInitialValues({ item: delivery, fields: board.delivery_config.fields })}
            disabledFields={calculateDisabledFieldsForDelivery({
              permissions: delivery.permissions,
              fields: board.delivery_config.fields,
            })}
            requiredFields={getRequiredFields({ item: delivery, fields: board.delivery_config.fields })}
            type={board.type}
            fieldConfig={board.delivery_config}
            onCancel={handleCancel}
            onSubmit={handleSubmit}
            deleteIcon={deleteIconButton(delivery)}
            restrictions={{
              'task-done': () => ({}),
              'task-responsible': () => ({}),
              'rich-text': () => ({}),
              'auto-number': () => ({}),
              'unique-document-id': () => ({}),
              'sequence-number': () => ({}),
              member: () => ({}),
              list: () => ({}),
              date: (fieldConfig, field) => {
                if (fieldConfig.delivery_met_date_field === field.id) {
                  return { disableFuture: true };
                }
              },
              string: () => ({}),
              numeric: () => ({}),
            }}
            metaInfo={getCardMetaInfo(delivery, roomMembers)}
            possibleResponsible={possibleResponsible}
            submitOptions={[
              {
                title: intl.get('common.form.save_and_close'),
                default: true,
                handleClick: () => handleSaveClick(true),
              },
              {
                title: intl.get('common.form.save'),
                handleClick: () => handleSaveClick(false),
              },
            ]}
          />
          <DeleteDeliveryDialogue
            open={openDeleteDialogue}
            onClose={() => setOpenDeleteDialogue(false)}
            onDelete={handleDelete}
            titles={{
              deleteDialogTitle: intl.get('board.tooltips.delivery.delete.question.title'),
              deleteDialogMessage: intl.get('board.tooltips.delivery.delete.question.message'),
            }}
          />
          <DeliveryAttachments
            activeCommunity={communityId}
            activeRoom={roomId}
            item={delivery}
            attachments={attachments}
            members={roomMembers}
            isUploading={isUploadingAttachments}
            isDeleting={isDeletingAttachments}
            onUploadAttachment={handleUploadAttachment}
            onDeleteAttachment={handleDeleteAttachment}
            onDownloadAttachment={handleDownloadAttachment}
            onAddLinks={handleAddLinks}
          />
          <Log board={board} log={log} members={roomMembers} item={delivery} />
        </Grid>
        <Grid size={{ sm: 12, md: 4, xs: 12 }}>
          <DeliveryComments
            board={board}
            item={delivery}
            comments={comments}
            onSubmitComment={handleSubmitComment}
            onUpdateComment={handleUpdateComment}
            onDeleteComment={handleDeleteComment}
            members={roomMembers}
          />
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(DeliveryUpdateContainer);
